import type { JSX } from 'react';

import type { Hero } from '@schema/index.js';
import type { Tile as TileSchema } from '@schema/tiles.js';

import { HeroFeatured } from './heroes/HeroFeatured.js';
import { HeroClock } from './heroes/HeroClock.js';
import { HeroRichText } from './heroes/HeroRichText.js';
import { HeroWeather } from './heroes/HeroWeather.js';

export interface LauncherHeroProps {
  /**
   * Hero variant + its content. Optional; treated as
   * `{ kind: 'featured' }` so configs from before profile.hero
   * existed keep their pre-hero render.
   */
  hero?: Hero;
  /**
   * Optional Pro/Ent hero background image, already resolved to a
   * loadable URL. ProfileView gates this on the `theming` feature
   * flag — pass `undefined` for Core deployments even when the
   * config has `heroImageRef` set. Honored by every variant that
   * renders a background slab (every variant except `none`).
   */
  imageSrc?: string;
  /** Used by the `featured` variant. */
  featuredTiles: readonly TileSchema[];
  /** Dispatch handler shared with the main grid. */
  onTileActivate: (tile: TileSchema) => Promise<void> | void;
}

/**
 * Hero band dispatcher. Switches on `hero.kind` and renders the matching
 * sub-component. `none` returns null so ProfileView's grid-section can
 * absorb the freed vertical space (CSS modifier on .profile-view__main
 * keyed by data-hero="none" — see styles/index.css).
 *
 * Why a dispatcher in this file instead of one big component: each
 * variant has its own concerns (clock tick, weather fetch, tiptap
 * render) that don't share enough code to merit a single component.
 * Dispatching here keeps ProfileView agnostic of variant internals.
 */
export function LauncherHero({
  hero,
  imageSrc,
  featuredTiles,
  onTileActivate,
}: LauncherHeroProps): JSX.Element | null {
  const variant: Hero = hero ?? { kind: 'featured' };

  if (variant.kind === 'none') return null;

  if (variant.kind === 'clock') {
    return (
      <HeroClock
        {...(variant.showDate !== undefined && { showDate: variant.showDate })}
        {...(variant.caption !== undefined && { caption: variant.caption })}
        {...(imageSrc !== undefined && { imageSrc })}
      />
    );
  }

  if (variant.kind === 'weather') {
    return (
      <HeroWeather
        {...(variant.showDate !== undefined && { showDate: variant.showDate })}
        {...(variant.latitude !== undefined && { latitude: variant.latitude })}
        {...(variant.longitude !== undefined && { longitude: variant.longitude })}
        {...(variant.locationLabel !== undefined && { locationLabel: variant.locationLabel })}
        {...(imageSrc !== undefined && { imageSrc })}
      />
    );
  }

  if (variant.kind === 'richText') {
    return (
      <HeroRichText
        contentJson={variant.contentJson}
        {...(imageSrc !== undefined && { imageSrc })}
      />
    );
  }

  // featured (default / fallback)
  return (
    <HeroFeatured
      featuredTiles={featuredTiles}
      {...(imageSrc !== undefined && { imageSrc })}
      onTileActivate={onTileActivate}
    />
  );
}
